import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import FeedbackCharts from './components/FeedbackCharts';
import Odometer from './components/Odometer';
import './ClientFeedbackPage.css';
import TextField from '@mui/material/TextField';
import OptInsModal from './OptInsModal';
import MenuItem from '@mui/material/MenuItem';
import { FaSyncAlt } from "react-icons/fa";
import he from 'he';

function ClientFeedbackPage() {
  // Using slug from URL
  const { slug } = useParams();

  const [clientDetails, setClientDetails] = useState(null);
  const [feedbacks, setFeedbacks] = useState([]);
  const [filteredFeedbacks, setFilteredFeedbacks] = useState([]);
  
  const [managers, setManagers] = useState([]);
  const [selectedManager, setSelectedManager] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Widget / chart data states
  const [feedbackVolume, setFeedbackVolume] = useState(0);
  const [inquiryCategoryVolume, setInquiryCategoryVolume] = useState({});
  const [managerResponseVolume, setManagerResponseVolume] = useState(0);
  const [positivePercentage, setPositivePercentage] = useState(0);
  const [neutralPercentage, setNeutralPercentage] = useState(0);
  const [negativePercentage, setNegativePercentage] = useState(0);

  // Other filters
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [keyword, setKeyword] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(""); // "" means "All"

  // Paging
  const [currentPage, setCurrentPage] = useState(1);
  const [feedbacksPerPage] = useState(10);

  // Sentiment updating
  const [sentimentUpdating, setSentimentUpdating] = useState(false);

  // Modal
  const [showOptIns, setShowOptIns] = useState(false);
  const openOptInsModal = () => setShowOptIns(true);
  const closeOptInsModal = () => setShowOptIns(false);

  useEffect(() => {
    
    // 1) Fetch the client by slug, get numeric ID
    const fetchClientDetailsBySlug = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/clients.php?slug=${slug}`
        );
        const data = await response.json();
        if (response.ok && data) {
          if (data.id) {
            setClientDetails(data);
            // Now that we have the numeric ID, fetch the feedback
            fetchFeedbacksByClientId(data.id);
          } else {
            setError("Failed to load client details (no ID).");
            setLoading(false);
          }
        } else {
          setError("Failed to load client details.");
          setLoading(false);
        }
      } catch (error) {
        setError("An error occurred while fetching client details.");
        setLoading(false);
      }
    };

    // 2) Once we know the numeric client_id, fetch feedback
    const fetchFeedbacksByClientId = async (numericId) => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/feedback.php?client_id=${numericId}`
        );
    
        const text = await response.text(); // Get the raw response
        console.log("Raw JSON response:", text); // Debugging
    
        const data = JSON.parse(text); // Parse JSON response
    
        if (response.ok) {
          if (!data.records) {
            throw new Error("Missing 'records' in response JSON.");
          }
    
          
          let feedbackData = data.records.map(fb => ({
            ...fb,
            text: he.decode(fb.text) // Decode HTML entities
          }));

          const uniqueFeedbacks = Array.from(
            new Map(feedbackData.map((fb) => [fb.id, fb])).values()
          );
    
          setFeedbacks(feedbackData);
          setFeedbacks(uniqueFeedbacks); // Set unique feedbacks only
    
          const uniqueCategories = new Set();
          uniqueFeedbacks.forEach((fb) => {
            if (Array.isArray(fb.categories)) {
              fb.categories.forEach((cat) => uniqueCategories.add(cat.trim()));
            } else if (fb.category) {
              uniqueCategories.add(fb.category.trim());
            }
          });
    
          setCategories([...uniqueCategories]);
    
          const uniqueManagers = [
            ...new Map(uniqueFeedbacks.map((fb) => [fb.manager?.id, fb.manager])).values(),
          ].filter((manager) => manager?.id);
          setManagers(uniqueManagers);
    
          filterFeedbacks(uniqueFeedbacks);
        } else {
          throw new Error(data.message || "Failed to load feedback.");
        }
      } catch (error) {
        console.error("Error parsing feedback:", error.message);
        setError("An error occurred while fetching feedback.");
      } finally {
        setLoading(false);
      }
    };    
    

    if (slug) {
      fetchClientDetailsBySlug();
    }
  }, [slug]);

  // Handle sentiment updates
  const handleSentimentChange = async (feedbackId, newSentiment) => {
    setSentimentUpdating(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/feedback.php`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: feedbackId, sentiment: newSentiment }),
        }
      );

      if (response.ok) {
        // Update local state
        const updatedFeedbacks = feedbacks.map((feedback) =>
          feedback.id === feedbackId
            ? { ...feedback, sentiment: newSentiment }
            : feedback
        );
        setFeedbacks(updatedFeedbacks);
        filterFeedbacks(updatedFeedbacks); // re-filter to recalc
      } else {
        alert('Failed to update sentiment.');
      }
    } catch (error) {
      alert('An error occurred while updating the sentiment.');
    } finally {
      setSentimentUpdating(false);
    }
  };

  // The main filter function – updated for multi‑categories
  const filterFeedbacks = (feedbackList = feedbacks) => {
    const filtered = feedbackList.filter((feedback) => {
      const feedbackDate = new Date(feedback.date_submitted);
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;

      // Keyword filter
      const keywordMatch = feedback.text
        .toLowerCase()
        .includes(keyword.toLowerCase());

      // Manager filter
      const managerMatch =
        selectedManager === null ||
        (feedback.manager && feedback.manager.id === selectedManager);

      // Category filter (handles comma-separated multiple categories)
      let categoryMatch = false;
      if (selectedCategory === '') {
        categoryMatch = true;
      } else if (feedback.categories) {
        // Split and trim categories then check if the selected category is included
        const cats = Array.isArray(feedback.categories) 
    ? feedback.categories.map(cat => cat.trim()) 
    : feedback.category ? [feedback.category.trim()] : [];
        categoryMatch = cats.includes(selectedCategory);
      } else if (feedback.category) {
        categoryMatch = feedback.category === selectedCategory;
      }

      // Date range filter
      const dateInRange =
        (!start || feedbackDate >= start) &&
        (!end || feedbackDate <= end);

      return dateInRange && keywordMatch && managerMatch && categoryMatch;
    });
    
    // Update the filteredFeedbacks list
    setFilteredFeedbacks(filtered);

    // Recalculate widget data based on the new filtered list

    // 1. Feedback volume
    setFeedbackVolume(filtered.length);

    // 2. Category volume (count each category from multi-category fields)
    const categoryCounts = filtered.reduce((acc, fb) => {
      if (fb.categories) {
        fb.categories.forEach((cat) => {
          const trimmedCat = cat.trim();
          acc[trimmedCat] = (acc[trimmedCat] || 0) + 1;
        });
      }
       else if (fb.category) {
        acc[fb.category] = (acc[fb.category] || 0) + 1;
      }
      return acc;
    }, {});
    setInquiryCategoryVolume(categoryCounts);

    // 3. Manager responses
    const managerResponses = filtered.filter(
      (fb) => fb.manager && fb.manager.id
    ).length;
    setManagerResponseVolume(managerResponses);

    // 4. Sentiment percentages
    const total = filtered.length;
    if (total > 0) {
      const positiveCount = filtered.filter(
        (fb) => fb.sentiment === 'Positive'
      ).length;
      const neutralCount = filtered.filter(
        (fb) => fb.sentiment === 'Neutral'
      ).length;
      const negativeCount = filtered.filter(
        (fb) => fb.sentiment === 'Negative'
      ).length;

      setPositivePercentage(((positiveCount / total) * 100).toFixed(2));
      setNeutralPercentage(((neutralCount / total) * 100).toFixed(2));
      setNegativePercentage(((negativeCount / total) * 100).toFixed(2));
    } else {
      setPositivePercentage(0);
      setNeutralPercentage(0);
      setNegativePercentage(0);
    }
    setCurrentPage(1);
  };

  // Handlers for each filter
  const handleSearchChange = (e) => {
    setKeyword(e.target.value);
    filterFeedbacks();
  };

  const handleRefreshFilters = () => {
    setStartDate("");
    setEndDate("");
    setKeyword("");
    setSelectedManager(null);
    setSelectedCategory("");
    setCurrentPage(1);
    filterFeedbacks();
  };

  const handleDateChange = () => {
    filterFeedbacks();
  };

  const handleManagerChange = (e) => {
    const value = e.target.value;
    setSelectedManager(value === "" ? null : parseInt(value, 10));
    filterFeedbacks();
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    filterFeedbacks();
  };

  // Pagination
  const indexOfLastFeedback = currentPage * feedbacksPerPage;
  const indexOfFirstFeedback = indexOfLastFeedback - feedbacksPerPage;
  const currentFeedbacks = filteredFeedbacks.slice(
    indexOfFirstFeedback,
    indexOfLastFeedback
  );
  const totalPages = Math.ceil(filteredFeedbacks.length / feedbacksPerPage);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  // For decoding HTML text (optional)
  function decodeHTMLEntities(str) {
    const txt = document.createElement('textarea');
    txt.innerHTML = str;
    return txt.value;
  }

  const getSentimentClass = (sentiment) => {
    switch (sentiment) {
      case 'Positive':
        return 'sentiment-positive';
      case 'Neutral':
        return 'sentiment-neutral';
      case 'Negative':
        return 'sentiment-negative';
      default:
        return '';
    }
  };

  // Renders the widget (top boxes)
  const renderWidgets = () => (
    <div className="widget-grid">
      <div className="widget-container">
        <div className="widget">
          <h3>Average Feedback</h3>
          <p>Positive: {positivePercentage}%</p>
          <p>Neutral: {neutralPercentage}%</p>
          <p>Negative: {negativePercentage}%</p>
        </div>
        <div className="widget">
          <h3>Total Submissions</h3>
          <Odometer value={feedbackVolume} />
        </div>
        <div className="widget">
          <h3>Manager Responses</h3>
          <Odometer value={managerResponseVolume} />
        </div>
      </div>

      <div className="inquiry-widget">
        <h3>Inquiry Categories</h3>
        {Object.entries(inquiryCategoryVolume).map(([category, count]) => (
          <p className="inquiry-widget-row" key={category}>
            <span>{category}:</span>
            <span>{count}</span>
          </p>
        ))}
      </div>
    </div>
  );

  return (
    <div className="client-feedback-container">
      <div className="overlay">
        <div className="feedback-container">
          {/* Show the logo if available */}
          {clientDetails?.logo && (
            <div className="client-logo-container">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/${clientDetails.logo}`}
                alt={`${clientDetails.name} Logo`}
                className="client-logo"
              />
            </div>
          )}

          <div className="date-filter">
            {/* Search by keyword */}
            <div className="keyword-search">
              <TextField
                id="keyword"
                label="Search"
                variant="outlined"
                className="keyword-search-input"
                value={keyword}
                onChange={handleSearchChange}
                InputLabelProps={{ style: { color: 'white' } }}
              />
            </div>

            {/* Filter by Manager */}
            <div className="keyword-search">
              <TextField
                select
                label="Filter by Manager"
                value={selectedManager ?? ""}
                onChange={handleManagerChange}
                variant="outlined"
                className="keyword-search-input"
                InputLabelProps={{ style: { color: 'white' } }}
                sx={{
                  "& .MuiOutlinedInput-root": { color: "white" },
                  "& .MuiSelect-icon": { color: "white" },
                  "& .MuiMenuItem-root": { color: "white" },
                }}
              >
                <MenuItem value="">All Managers</MenuItem>
                {managers.map((manager) => (
                  <MenuItem key={manager.id} value={manager.id}>
                    {manager.name}
                  </MenuItem>
                ))}
              </TextField>
            </div>

            {/* Filter by Category */}
            <div className="keyword-search">
              <TextField
                select
                label="Filter by Category"
                value={selectedCategory}
                onChange={handleCategoryChange}
                variant="outlined"
                className="keyword-search-input"
                InputLabelProps={{ style: { color: 'white' } }}
                sx={{
                  "& .MuiOutlinedInput-root": { color: "white" },
                  "& .MuiSelect-icon": { color: "white" },
                  "& .MuiMenuItem-root": { color: "white" },
                }}
              >
                <MenuItem value="">All Categories</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </TextField>
            </div>

            {/* Date Range Filter */}
            <div className="date-filter-row">
              <label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </label>
              <label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </label>
              <button onClick={handleRefreshFilters} className="refresh-button">
                <FaSyncAlt />
              </button>
              <button onClick={handleDateChange}>Filter</button>
            </div>
          </div>
        </div>

        {/* Optional: Opt-Ins Modal */}
        {showOptIns && (
          <OptInsModal
            clientId={clientDetails?.id}
            onClose={closeOptInsModal}
          />
        )}

        {renderWidgets()}

        {filteredFeedbacks.length > 0 && (
          <div className="chart-container">
            <FeedbackCharts feedbacks={filteredFeedbacks} />
          </div>
        )}

        {loading ? (
          <p>Loading feedback...</p>
        ) : error ? (
          <p className="error-message">{error}</p>
        ) : (
          <div className="feedback-content">
            <ul className="feedback-list">
              {currentFeedbacks.map((feedback) => (
                <li key={feedback.id} className="feedback-item">
                  <div className="feedback-list-content">
                    <p className="feedback-dates">
                      Date Submitted: {feedback.date_submitted}
                    </p>
                    <p className="feedback-dates">Name: {feedback.name}</p>
                    <p className="feedback-dates">Email: {feedback.email}</p>
                    <p className="feedback-dates">Phone: {feedback.phone}</p>
                    <p>{decodeHTMLEntities(feedback.text)}</p>

                    {feedback.manager && feedback.manager.id && (
                      <p className="feedback-manager">
                        <strong>Associated Manager:</strong>{" "}
                        {feedback.manager.name}
                      </p>
                    )}
                    <div className="category-tags">
                      {feedback.categories && feedback.categories.map((cat, index) => (
                        <span key={index} className="category-tag">{cat}</span>
                      ))}
                    </div>

                  </div>

                  <div className="feedback-actions">
                    <select
                      className={`sentiment-select ${getSentimentClass(
                        feedback.sentiment
                      )}`}
                      value={feedback.sentiment}
                      disabled={sentimentUpdating}
                      onChange={(e) =>
                        handleSentimentChange(feedback.id, e.target.value)
                      }
                    >
                      <option value="Positive">Positive</option>
                      <option value="Neutral">Neutral</option>
                      <option value="Negative">Negative</option>
                    </select>
                  </div>
                </li>
              ))}
            </ul>

            {/* Simple Pagination */}
            <div className="pagination">
              <button
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                Prev
              </button>
              <span>
                {currentPage} / {totalPages}
              </span>
              <button
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                Next
              </button>
            </div>
            <p className='review-total'>Total Reviews: {filteredFeedbacks.length}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default ClientFeedbackPage;
